import { AnyAction } from 'redux';
import { SagaIterator } from 'redux-saga';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { orderingCalc } from 'src/api/cart';
import { fetchMyOffers, fetchOffers, submitAndPayOffer, submitOffer } from 'src/api/offer';
import { ROUTE_ACCOUNT_OFFERS } from 'src/config/account-routes';
import { CartOfferRequestDTO } from 'src/entities/cart/request/CartOfferRequestDTO';
import CartRequestDTO from 'src/entities/cart/request/CartRequestDTO';
import {
    clearShoppingCart,
    failureOrderingCalc,
    requestOrderingCalc,
    successOrderingCalc,
} from 'src/store/cart/actions';
import { getShoppingCart, ShoppingCartEntityState } from 'src/store/cart/selectors';
import { ROUTE_CART, ROUTE_CHECKOUT, ROUTE_DIRECTSELL_CONFIRMATION } from '../../config/routes';
import {
    APPLY_OFFER,
    CLEAR_MY_OFFERS,
    EDIT_OFFER,
    FETCH_MY_OFFERS,
    FETCH_OFFERS,
    requestMyOffers,
    requestMyOffersFailure,
    requestMyOffersSuccess,
    requestOffers,
    requestOffersFailure,
    requestOffersSuccess,
    SUBMIT_AND_PAY_OFFER,
    SUBMIT_OFFER,
    UPDATE_OFFER,
} from './action';

/**
 * Edit Offer
 */
function* editOfferSaga(action: AnyAction): SagaIterator {
    yield put(requestOrderingCalc());

    const cartOfferRequestDTO = CartOfferRequestDTO.fromOffer(action.offer);

    const { error, response } = yield call(orderingCalc, cartOfferRequestDTO);

    if (error) yield put(failureOrderingCalc(error));
    else {
        yield put(successOrderingCalc(response, false));
        window.location.replace(ROUTE_CART.path);
    }
}

export function* generateEditOfferWatcher() {
    yield takeLatest(EDIT_OFFER, editOfferSaga);
}

/**
 * Update Offer
 */
function* updateOffer(action: AnyAction): SagaIterator {
    yield put(requestOrderingCalc());

    const existingShoppingCart: ShoppingCartEntityState = yield select(getShoppingCart);
    const cartOfferRequestDTO = CartOfferRequestDTO.fromShoppingCart(existingShoppingCart.content, action.data);

    const { error, response } = yield call(orderingCalc, cartOfferRequestDTO);

    if (error) yield put(failureOrderingCalc(error));
    else yield put(successOrderingCalc(response, existingShoppingCart.content.containsOrderDirectlyProduct));
}

export function* generateUpdateOfferWatcher() {
    yield takeLatest(UPDATE_OFFER, updateOffer);
}

/**
 * Submit Offer
 */
function* submitOfferSaga(action: AnyAction): SagaIterator {
    yield put(requestOrderingCalc());

    const existingShoppingCart: ShoppingCartEntityState = yield select(getShoppingCart);
    const cartOfferRequestDTO = CartOfferRequestDTO.fromShoppingCart(existingShoppingCart.content, action.data);

    const { error } = yield call(submitOffer, cartOfferRequestDTO);

    if (error) yield put(failureOrderingCalc(error));
    else {
        yield put(
            clearShoppingCart(() => {
                window.location.replace(ROUTE_ACCOUNT_OFFERS.path);
            })
        );
    }
}

export function* generateSubmitOfferWatcher() {
    yield takeLatest(SUBMIT_OFFER, submitOfferSaga);
}

/**
 * Submit Offer
 */
function* submitAndPayOfferSaga(action: AnyAction): SagaIterator {
    yield put(requestOrderingCalc());

    const existingShoppingCart: ShoppingCartEntityState = yield select(getShoppingCart);
    const cartOfferRequestDTO = CartOfferRequestDTO.fromShoppingCart(existingShoppingCart.content, action.data);

    const { error, response } = yield call(submitAndPayOffer, cartOfferRequestDTO);

    if (error) yield put(failureOrderingCalc(error));
    else {
        const { paymentLink, orderId } = response.data;
        yield put(
            clearShoppingCart(() => {
                window.location.replace(
                    ROUTE_DIRECTSELL_CONFIRMATION.interpolatedClone(undefined, { paymentLink, orderId }).path
                );
            })
        );
    }
}

export function* generateSubmitAndPayOfferWatcher() {
    yield takeLatest(SUBMIT_AND_PAY_OFFER, submitAndPayOfferSaga);
}

/**
 * Apply Offer
 */
function* applyOfferSaga(action: AnyAction): SagaIterator {
    yield put(requestOrderingCalc());

    const cartOfferRequestDTO = CartRequestDTO.fromExistingOffer(action.offer);

    const { error, response } = yield call(orderingCalc, cartOfferRequestDTO);

    if (error) yield put(failureOrderingCalc(error));
    else {
        yield put(successOrderingCalc(response, true));
        window.location.replace(ROUTE_CHECKOUT.path);
    }
}

export function* generateApplyOfferWatcher() {
    yield takeLatest(APPLY_OFFER, applyOfferSaga);
}

/**
 * Fetch my offers
 */
function* fetchMyOffersSaga(action: AnyAction): SagaIterator {
    yield put(requestMyOffers());

    const { error, response } = yield call(fetchMyOffers);

    if (error) yield put(requestMyOffersFailure(error));
    else yield put(requestMyOffersSuccess(response));
}

export function* generateFetchMyOffersWatcher() {
    yield takeLatest(FETCH_MY_OFFERS, fetchMyOffersSaga);
}

/**
 * Clear My Offers
 */
function* clearMyOffersSaga(action: AnyAction): SagaIterator {
    yield put(requestMyOffersSuccess({ response: { data: [] } }));
}

export function* generateClearMyOffersWatcher() {
    yield takeLatest(CLEAR_MY_OFFERS, clearMyOffersSaga);
}

/**
 * Fetch offers
 */
function* fetchOffersSaga(action: AnyAction): SagaIterator {
    yield put(requestOffers());

    const { error, response } = yield call(fetchOffers);

    if (error) yield put(requestOffersFailure(error));
    else yield put(requestOffersSuccess(response));
}

export function* generateFetchOffersWatcher() {
    yield takeLatest(FETCH_OFFERS, fetchOffersSaga);
}
